import { History, Transition } from 'history';
import { useCallback, useContext, useEffect } from 'react';
import {
  UNSAFE_NavigationContext as NavigationContext,
  Navigator,
} from 'react-router-dom';
import { useLatest } from 'react-use';

import { appActions } from 'app/app.actions';
import { useAppDispatch } from 'utils/redux/useAppDispatch';

type NavigatorWithBlock = Navigator & Pick<History, 'block'>;

/**
 * A hook that blocks navigation attempts. This is useful for preventing leaving
 * a page until some condition is met, like saving form data.
 *
 * This implementation is based on the now removed `useBlocker` and `usePrompt`
 * hooks from react-router-dom. As things stand, it doesn't seem react-router
 * will be adding these back, so we have to maintain these ourselves or consider
 * other implementations, because these hooks rely on `navigator.block` method
 * which is only available with `unstable_HistoryRouter` router.
 *
 * @param when Boolean indicating whether the prompt should be trigger upon
 *   navigation.
 * @param message Custom text message to display in the prompt.
 * @param callbacks Optional callbacks for tracking or side effects when the
 *   prompt is shown, confirmed or cancelled.
 *
 *   See below links for more context:
 * @see https://github.com/remix-run/react-router/issues/8139
 * @see https://github.com/remix-run/react-router/issues/8139#issuecomment-1247080906
 */
type NavigatePromptCallbacks = {
  onPrompted?: () => void;
  onConfirmed?: () => void;
  onCancelled?: () => void;
};

export const useNavigatePrompt = (
  when = true,
  message = 'When you leave this page, all unsaved changes will be lost.',
  callbacks?: NavigatePromptCallbacks,
): void => {
  const dispatch = useAppDispatch();
  // Keep latest callbacks while preserving stable blocker identity.
  const latestCallbacks = useLatest(callbacks);

  const blocker = useCallback(
    async (tx: Transition) => {
      latestCallbacks.current?.onPrompted?.();

      const { isConfirmed } = await dispatch(
        appActions.showModal({
          modal: {
            type: 'app:confirm',
            arg: {
              title: 'Sure you want to leave?',
              description: message,
              confirm: {
                label: 'Leave',
                variant: 'primary',
              },
            },
          },
        }),
      );

      if (isConfirmed) {
        latestCallbacks.current?.onConfirmed?.();
        tx.retry();
      } else {
        latestCallbacks.current?.onCancelled?.();
      }
    },
    [dispatch, latestCallbacks, message],
  );

  useNavigateBlocker(blocker, when);
};

const useNavigateBlocker = (blocker: (tx: Transition) => void, when = true) => {
  const { navigator } = useContext(NavigationContext);

  useEffect(() => {
    if (!when) return;

    const unblock = (navigator as NavigatorWithBlock).block((tx) => {
      const autoUnblockingTx = {
        ...tx,
        retry() {
          unblock();
          tx.retry();
        },
      };

      blocker(autoUnblockingTx);
    });

    return unblock;
  }, [navigator, blocker, when]);
};
